import Chip, { type ChipProps } from "@mui/material/Chip";
import { useTranslate } from "@refinedev/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EditIcon from "@mui/icons-material/Edit";
import PaymentIcon from "@mui/icons-material/Payment";
import ArchiveIcon from "@mui/icons-material/Archive";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useTheme } from "@mui/material/styles";
import { green, orange, blue, grey, red, amber } from "@mui/material/colors";
import type { PropertyStatus } from "../../../interfaces/property";

type Props = {
  value?: PropertyStatus;
  size?: ChipProps["size"];
};

export const PropertyStatusChip = (props: Props) => {
  const t = useTranslate();
  const { palette } = useTheme();
  const isDarkMode = palette.mode === "dark";

  // Default to 'draft' if no status provided
  const status = props.value || 'draft';

  // Define colors and icons for each status
  // Fallback for unknown statuses (e.g., legacy 'ready_to_publish', 'paused')
  const defaultConfig = {
    color: isDarkMode ? grey[400] : grey[700],
    icon: <EditIcon sx={{ fill: isDarkMode ? grey[400] : grey[700] }} />,
  };

  const statusConfig: Record<string, { color: string; icon: JSX.Element }> = {
    draft: {
      color: isDarkMode ? grey[400] : grey[700],
      icon: <EditIcon sx={{ fill: isDarkMode ? grey[400] : grey[700] }} />,
    },
    waiting_for_payment: {
      color: isDarkMode ? orange[300] : orange[700],
      icon: <HourglassEmptyIcon sx={{ fill: isDarkMode ? orange[300] : orange[700] }} />,
    },
    payment_successful: {
      color: isDarkMode ? green[300] : green[700],
      icon: <PaymentIcon sx={{ fill: isDarkMode ? green[300] : green[700] }} />,
    },
    payment_error: {
      color: isDarkMode ? red[300] : red[700],
      icon: <ErrorOutlineIcon sx={{ fill: isDarkMode ? red[300] : red[700] }} />,
    },
    published: {
      color: isDarkMode ? green[200] : green[800],
      icon: <CheckCircleIcon sx={{ fill: isDarkMode ? green[200] : green[600] }} />,
    },
    archived: {
      color: isDarkMode ? grey[500] : grey[600],
      icon: <ArchiveIcon sx={{ fill: isDarkMode ? grey[500] : grey[600] }} />,
    },
    expired: {
      color: isDarkMode ? amber[400] : amber[800],
      icon: <EventBusyIcon sx={{ fill: isDarkMode ? amber[400] : amber[800] }} />,
    },
  };

  const config = statusConfig[status] || defaultConfig;

  return (
    <Chip
      label={t(`properties.status.${status}`)}
      icon={config.icon}
      variant="outlined"
      size={props?.size || "small"}
      sx={{
        borderColor: config.color,
        color: config.color,
      }}
    />
  );
};
